import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ListMusic, Check, X, RefreshCw, Clock, User, MessageSquare, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DJRequestQueue() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [stats, setStats] = useState({ pending: 0, played: 0, rejected: 0 });

  const fetchRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/requests/pending`);
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.log('Failed to fetch requests');
    }
    setLoading(false);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/requests/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.log('Failed to fetch stats');
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchRequests();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchRequests, fetchStats]);

  const handleUpdateStatus = async (requestId, status) => {
    setUpdating(requestId);
    try {
      const response = await fetch(`${API_URL}/api/requests/${requestId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        // Remove from list
        setRequests(prev => prev.filter(r => r.request_id !== requestId));
        fetchStats();
      }
    } catch (error) {
      console.log('Failed to update request');
    }
    setUpdating(null);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000); // minutes
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Only show for DJ/Admin/Mod
  const canManage = user && ['dj', 'admin', 'mod'].includes(user.role);

  if (!canManage) {
    return null;
  }

  return (
    <div 
      className="glass rounded-2xl p-4"
      data-testid="dj-request-queue"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListMusic className="w-5 h-5" style={{ color: 'var(--primary)' }} />
          <h3 
            className="text-lg font-bold"
            style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
          >
            Request Queue
          </h3>
          {requests.length > 0 && (
            <span 
              className="px-2 py-0.5 rounded-full text-xs font-bold"
              style={{ backgroundColor: 'var(--primary)', color: 'white' }}
            >
              {requests.length}
            </span>
          )}
        </div>
        
        <button
          onClick={() => { fetchRequests(); fetchStats(); }}
          className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
          style={{ color: 'var(--text-muted)' }}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-4 text-xs">
        <div className="flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <Clock className="w-3 h-3" />
          <span>{stats.pending} pending</span>
        </div>
        <div className="flex items-center gap-1" style={{ color: '#22C55E' }}>
          <Check className="w-3 h-3" />
          <span>{stats.played} played</span>
        </div>
        <div className="flex items-center gap-1" style={{ color: '#EF4444' }}>
          <X className="w-3 h-3" />
          <span>{stats.rejected} skipped</span>
        </div>
      </div>

      {/* Request List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-6">
          <ListMusic className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            No pending requests
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {requests.map((req) => (
            <div 
              key={req.request_id}
              className="p-3 rounded-xl"
              style={{ backgroundColor: 'var(--surface-highlight)' }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p 
                    className="font-medium text-sm truncate"
                    style={{ color: 'var(--text-main)' }}
                  >
                    {req.title}
                  </p>
                  <p 
                    className="text-xs truncate"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {req.artist}
                  </p>
                  
                  {/* Message if present */}
                  {req.message && (
                    <div 
                      className="mt-2 p-2 rounded-lg text-xs flex items-start gap-1"
                      style={{ backgroundColor: 'var(--surface)' }}
                    >
                      <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary)' }} />
                      <span style={{ color: 'var(--text-muted)' }}>{req.message}</span>
                    </div>
                  )}
                  
                  {/* Meta info */}
                  <div className="flex items-center gap-2 mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {req.requested_by_name || 'Anonymous'}
                    </span>
                    <span>•</span>
                    <span>{formatTime(req.created_at)}</span>
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleUpdateStatus(req.request_id, 'played')}
                    disabled={updating === req.request_id}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}
                    title="Mark as played"
                  >
                    {updating === req.request_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleUpdateStatus(req.request_id, 'rejected')}
                    disabled={updating === req.request_id}
                    className="p-2 rounded-lg transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}
                    title="Skip request"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
